import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2, Phone, Send, ShieldCheck, Timer, Zap } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/landing/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLAN_OPTIONS = ["Essentiel", "Croissance & Sérénité", "Je ne sais pas encore"];

const highlights = [
  { icon: Timer, text: "Réponse en moins de 4 heures ouvrables" },
  { icon: Zap, text: "Maquette stratégique offerte, sans engagement" },
  { icon: Phone, text: "+1 438 969 6957", href: "tel:+14389696957" },
  { icon: ShieldCheck, text: "Données 100% confidentielles. Aucun spam." },
];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-gray-500 outline-none transition-colors duration-300 focus:border-emerald-400/60 focus:bg-white/[0.06]";

export default function Contact({ selectedPlan, setSelectedPlan }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, plan: selectedPlan });
      setSent(true);
      toast.success("Demande envoyée ! Nous revenons vers vous sous 4h ouvrables.");
    } catch {
      toast.error("Une erreur est survenue. Réessayez dans un instant.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden py-20 lg:py-32" data-testid="contact-section">
      <div className="pointer-events-none absolute bottom-0 left-1/2 h-[400px] w-[800px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[140px]" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
        <Reveal>
          <p className="font-mono-label mb-4">Chapitre 04 — On lance ?</p>
          <h2 className="text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
            Prêt à lancer un site qui travaille pour vous{" "}
            <span className="text-emerald-400">24/7 ?</span>
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-gray-400 sm:text-lg">
            Remplissez le formulaire. Nous revenons vers vous avec une maquette
            stratégique offerte — avant même que vous ne payiez un dollar.
          </p>
          <ul className="mt-8 space-y-4">
            {highlights.map(({ icon: Icon, text, href }) => (
              <li key={text} className="flex items-center gap-3 text-sm text-gray-300">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/15">
                  <Icon className="h-4 w-4 text-emerald-400" />
                </span>
                {href ? (
                  <a href={href} data-testid="contact-phone-link" className="font-semibold text-emerald-300 transition-colors hover:text-emerald-200">
                    {text}
                  </a>
                ) : (
                  text
                )}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="glass emerald-glow rounded-2xl p-7 sm:p-9" data-testid="contact-form-card">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center py-14 text-center"
                data-testid="contact-success"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/20">
                  <Check className="h-8 w-8 text-emerald-400" />
                </span>
                <h3 className="mt-6 text-xl font-semibold text-white">Demande bien reçue !</h3>
                <p className="mt-2 max-w-xs text-sm text-gray-400">
                  Merci {form.name.split(" ")[0]}. Nous vous répondons à{" "}
                  <span className="text-emerald-300">{form.email}</span> sous 4h ouvrables.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">
                      Nom complet
                    </label>
                    <input
                      id="contact-name"
                      data-testid="contact-name-input"
                      required
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Marie Tremblay"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">
                      Adresse courriel
                    </label>
                    <input
                      id="contact-email"
                      data-testid="contact-email-input"
                      required
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="marie@entreprise.ca"
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-company" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">
                      Entreprise / Projet
                    </label>
                    <input
                      id="contact-company"
                      data-testid="contact-company-input"
                      value={form.company}
                      onChange={update("company")}
                      placeholder="Entreprise ABC"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-plan" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">
                      Forfait visé
                    </label>
                    <select
                      id="contact-plan"
                      data-testid="contact-plan-select"
                      value={selectedPlan}
                      onChange={(e) => setSelectedPlan(e.target.value)}
                      className={`${inputCls} appearance-none`}
                    >
                      {PLAN_OPTIONS.map((p) => (
                        <option key={p} value={p} className="bg-[#0F141C]">
                          {p}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-message" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">
                    Vos objectifs
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    rows={4}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Parlez-nous de votre entreprise et de ce que votre site doit accomplir…"
                    className={`${inputCls} resize-none`}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={loading}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-6 py-4 text-sm font-bold text-emerald-950 shadow-[0_8px_32px_rgba(16,185,129,0.35)] transition-all duration-300 hover:bg-emerald-400 disabled:opacity-60"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Valider ma demande de projet
                      <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-gray-500">
                  Données 100% confidentielles. Aucun spam.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
